import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import {
  listCourses,
  ListCoursesRequest,
} from "@/modules/learningCenter/features/CourseManagement/api/listCourses";
import { getCourseMetadata } from "@/modules/learningCenter/features/CourseManagement/api/getCourseMetadata";
import { publishCourse } from "@/modules/learningCenter/features/CourseManagement/api/publishCourse";
import {
  archiveCourse,
  ArchiveCourseResponse,
} from "@/modules/learningCenter/features/CourseManagement/api/archiveCourse";
import {
  CourseListItem,
  CourseMetadata,
} from "@/modules/learningCenter/features/CourseManagement/types/course";

interface CourseListState {
  courses: CourseListItem[];
  totalCount: number;

  fetchLoading: boolean;
  fetchError: string | null;

  metadata: CourseMetadata | null;
  metadataLoading: boolean;
  metadataError: string | null;

  publishLoading: boolean;
  archiveLoading: boolean;
}

const initialState: CourseListState = {
  courses: [],
  totalCount: 0,

  fetchLoading: false,
  fetchError: null,

  metadata: null,
  metadataLoading: false,
  metadataError: null,

  publishLoading: false,
  archiveLoading: false,
};

const courseListSlice = createSlice({
  name: "courseList",
  initialState,
  reducers: {
    setFetchLoading(state, action: PayloadAction<boolean>) {
      state.fetchLoading = action.payload;
    },
    setFetchError(state, action: PayloadAction<string | null>) {
      state.fetchError = action.payload;
    },
    setCourses(state, action: PayloadAction<CourseListItem[]>) {
      state.courses = action.payload;
    },
    setTotalCount(state, action: PayloadAction<number>) {
      state.totalCount = action.payload;
    },
    setMetadata(state, action: PayloadAction<CourseMetadata>) {
      state.metadata = action.payload;
    },
    setMetadataLoading(state, action: PayloadAction<boolean>) {
      state.metadataLoading = action.payload;
    },
    setMetadataError(state, action: PayloadAction<string | null>) {
      state.metadataError = action.payload;
    },
    setPublishLoading(state, action: PayloadAction<boolean>) {
      state.publishLoading = action.payload;
    },
    setArchiveLoading(state, action: PayloadAction<boolean>) {
      state.archiveLoading = action.payload;
    },
  },
});

export const {
  setFetchLoading,
  setFetchError,
  setCourses,
  setTotalCount,
  setMetadata,
  setMetadataLoading,
  setMetadataError,
  setPublishLoading,
  setArchiveLoading,
} = courseListSlice.actions;

export default courseListSlice.reducer;

export const fetchCoursesAction =
  (params: ListCoursesRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setFetchLoading(true));
    dispatch(setFetchError(null));
    try {
      const response = await listCourses(params);
      dispatch(setCourses(response.data));
      dispatch(
        setTotalCount(response.meta?.totalCount ?? response.data.length),
      );
    } finally {
      dispatch(setFetchLoading(false));
    }
  };

export const fetchCourseMetadataAction =
  (): AppThunk => async (dispatch: AppDispatch) => {
    dispatch(setMetadataLoading(true));
    dispatch(setMetadataError(null));
    try {
      const response = await getCourseMetadata();
      dispatch(setMetadata(response.data));
    } finally {
      dispatch(setMetadataLoading(false));
    }
  };

export const publishCourseAction =
  (uuid: string, onSuccess?: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setPublishLoading(true));
    try {
      await publishCourse(uuid);
      if (onSuccess) {
        onSuccess();
      }
    } finally {
      dispatch(setPublishLoading(false));
    }
  };

export const archiveCourseAction =
  (
    uuid: string,
    onSuccess?: (result: ArchiveCourseResponse["data"]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setArchiveLoading(true));
    try {
      const response = await archiveCourse(uuid);
      if (onSuccess) {
        onSuccess(response.data);
      }
    } finally {
      dispatch(setArchiveLoading(false));
    }
  };
